import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SearchField, Spinner } from '@forwardflow/ui'
import { Lead } from '@forwardflow/services/LeadService'
import { useGetRecentConversations } from '@forwardflow/hooks/customHooks/conversation/useGetRecentConversations'
import { PaginationQueryParams } from '@forwardflow/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { LeadListItem } from './LeadListItem'
import { useConversationSSE } from '@forwardflow/hooks/customHooks/conversation/useConversationSSE'
import { debounce } from 'lodash'
import { getStorageKey } from '@forwardflow/helpers/utils'
import { useAppStore } from '@forwardflow/store'
import { InfiniteScroll } from '@forwardflow/ui-v2'

interface LeadListProps {
   onSelectLead: (leadId: number | null) => void
   selectedStageFilter: string
   selectedCampaignIds: string[] | null
   selectedCampaignName: string | null
   selectedLeadFilter?: string
}

type NavigationState = 'idle' | 'checking' | 'ready'

export const LeadList: React.FC<LeadListProps> = ({
   onSelectLead,
   selectedStageFilter,
   selectedCampaignIds,
   selectedCampaignName,
   selectedLeadFilter,
}) => {
   const { id: leadId } = useParams()
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const isLoadingMoreRef = useRef(false)
   const hasMountedRef = useRef(false)
   const isFilterChangeRef = useRef(false)
   const [accumulatedLeads, setAccumulatedLeads] = useState<Lead[]>([])

   const [searchInput, setSearchInput] = useState('')
   const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
   const [page, setPage] = useState(1)
   const [navigationState, setNavigationState] =
      useState<NavigationState>('idle')
   const [initialLoad, setInitialLoad] = useState(true)
   const LEADS_LIMIT = 10
   const { setCurrentOrganization } = useAppStore()

   // Get organizationId from URL params
   const organizationId = searchParams.get('organizationId')

   // Set current organization when organizationId is present in URL
   useEffect(() => {
      if (organizationId) {
         setCurrentOrganization(organizationId)
      }
   }, [organizationId, setCurrentOrganization])

   // Stabilize campaignIds array to prevent unnecessary re-renders
   const campaignIdsString = useMemo(
      () =>
         selectedCampaignIds === null ? null : selectedCampaignIds.join(','),
      [selectedCampaignIds]
   )

   const queryParams: PaginationQueryParams = useMemo(() => {
      const params: PaginationQueryParams = {
         limit: LEADS_LIMIT,
         page,
         search: debouncedSearchTerm.trim() || undefined,
         hasAppointment:
            selectedStageFilter === 'ff-appointment-set' ? true : undefined,
         campaignId: campaignIdsString || undefined,
      }

      // Add lead filter parameters based on selection
      if (selectedLeadFilter && selectedLeadFilter !== 'allLeads') {
         switch (selectedLeadFilter) {
            case 'engagedLead':
               params.engagedLead = true
               break
            case 'liveTransfer':
               params.liveTransfer = true
               break
            case 'incomingCall':
               params.incomingCall = true
               break
            case 'callOver30s':
               params.callOver30s = true
               break
            case 'textConversations':
               params.textConversations = true
               break
            case 'escalated':
               params.isEscalated = true
               break
            case 'stopped':
               params.isStopped = true
               break
            default:
               // Event filter values are sent as callEventName
               params.callEventName = selectedLeadFilter
               break
         }
      }

      return params
   }, [
      page,
      debouncedSearchTerm,
      selectedStageFilter,
      campaignIdsString,
      selectedLeadFilter,
   ])

   const {
      leads,
      loading: loadingRecentConversations,
      isFetching,
      error,
      hasMore,
      mutate,
   } = useGetRecentConversations(queryParams)

   useConversationSSE(mutate)

   // Helper function to build URL with search params - memoized to prevent infinite loops
   const buildUrlWithParams = useCallback(
      (leadId?: number) => {
         const isDemo = window.location.pathname.includes(
            'demo-conversations-old'
         )
         const basePath = isDemo ? '/demo-conversations-old' : '/conversations'

         const params = new URLSearchParams()

         // Preserve organizationId if it exists
         if (organizationId) {
            params.set('organizationId', organizationId)
         }

         // Add stage filter if not default
         if (selectedStageFilter !== 'all-stages') {
            let stageParam = 'all-stages'
            if (selectedStageFilter === 'ff-appointment-set') {
               stageParam = 'apt-set'
            } else if (selectedStageFilter === 'ff-appointment-missed') {
               stageParam = 'apt-missed'
            }
            if (stageParam !== 'all-stages') {
               params.set('stages', stageParam)
            }
         }

         // Add campaign filter if exists
         if (selectedCampaignName) {
            params.set('filter', selectedCampaignName)
         }

         // Add lead filter if not default
         if (selectedLeadFilter && selectedLeadFilter !== 'allLeads') {
            params.set('leadFilter', selectedLeadFilter)
         }

         const queryString = params.toString()
         const urlSuffix = queryString ? `?${queryString}` : ''

         if (leadId) {
            return `${basePath}/${leadId}${urlSuffix}`
         }
         return `${basePath}${urlSuffix}`
      },
      [
         selectedStageFilter,
         selectedCampaignName,
         selectedLeadFilter,
         organizationId,
      ]
   )

   // Reset state when filters change
   useEffect(() => {
      if (!hasMountedRef.current) {
         hasMountedRef.current = true
         return
      }
      isFilterChangeRef.current = true
      setPage(1)
      setAccumulatedLeads([])
      setNavigationState('idle')
      setInitialLoad(true)
      isLoadingMoreRef.current = false
   }, [
      selectedCampaignIds,
      selectedStageFilter,
      debouncedSearchTerm,
      selectedLeadFilter,
   ])

   // Accumulate leads as they load
   useEffect(() => {
      if (leads?.length) {
         if (page === 1) {
            setAccumulatedLeads(leads)
            isLoadingMoreRef.current = false
         } else {
            setAccumulatedLeads((prev) => {
               const newUniqueLeads = leads.filter(
                  (newLead) =>
                     !prev.some(
                        (existingLead) => existingLead.leadId === newLead.leadId
                     )
               )
               return [...prev, ...newUniqueLeads]
            })
            isLoadingMoreRef.current = false
         }
      }

      if (!loadingRecentConversations) {
         setInitialLoad(false)
      }
   }, [leads, loadingRecentConversations, page])

   // Detect empty campaign filter (e.g., "All Active Campaigns" with no active campaigns)
   const isEmptyCampaignFilterRef = useRef(false)
   isEmptyCampaignFilterRef.current =
      Array.isArray(selectedCampaignIds) && selectedCampaignIds.length === 0

   // Consolidated navigation logic - handles all navigation scenarios
   useEffect(() => {
      // Wait for initial data load (but not when campaign filter is explicitly empty)
      if (
         (loadingRecentConversations && !isEmptyCampaignFilterRef.current) ||
         navigationState === 'ready'
      )
         return

      const storageKey = getStorageKey(
         selectedStageFilter,
         selectedCampaignName
      )
      const storedLeadId = localStorage.getItem(storageKey)

      // Case 1: No leads available (or empty campaign filter) - clear selection and navigate to base path
      if (!leads || leads.length === 0 || isEmptyCampaignFilterRef.current) {
         if (navigationState === 'idle') {
            navigate(buildUrlWithParams(), { replace: true })
            onSelectLead(null)
            setNavigationState('ready')
         }
         return
      }

      // Case 2: Lead ID exists in URL
      if (leadId) {
         const urlLeadExists = leads.some(
            (lead) => lead.leadId === Number(leadId)
         )
         if (urlLeadExists) {
            onSelectLead(Number(leadId))
            setNavigationState('ready')
            return
         }
         // On initial page load / deep-link, trust the URL even if lead
         // isn't in loaded page (conversation panel fetches independently)
         if (!isFilterChangeRef.current) {
            onSelectLead(Number(leadId))
            setNavigationState('ready')
            return
         }
         // On filter change, lead not in results - fall through to select first lead
      }

      // Case 3: No lead ID in URL - determine which lead to select
      if (navigationState === 'idle') {
         // Try stored lead first
         if (storedLeadId) {
            const storedLeadExists = leads.some(
               (lead) => lead.leadId === Number(storedLeadId)
            )
            if (storedLeadExists) {
               navigate(buildUrlWithParams(Number(storedLeadId)), {
                  replace: true,
               })
               onSelectLead(Number(storedLeadId))
               isFilterChangeRef.current = false
               setNavigationState('ready')
               return
            }
         }

         // Fallback to first lead
         navigate(buildUrlWithParams(leads[0].leadId), { replace: true })
         onSelectLead(leads[0].leadId)
         isFilterChangeRef.current = false
         setNavigationState('ready')
      }
   }, [
      leads,
      loadingRecentConversations,
      leadId,
      navigationState,
      selectedStageFilter,
      selectedCampaignName,
      buildUrlWithParams,
      navigate,
      onSelectLead,
   ])

   // Use useRef to store debounced function to avoid linter warnings
   const debouncedSetSearchRef = useRef(
      debounce((value: string) => {
         setDebouncedSearchTerm(value)
         setPage(1)
         setAccumulatedLeads([])
      }, 500)
   )

   const handleSearchChange = useCallback((value: string) => {
      setSearchInput(value)
      debouncedSetSearchRef.current(value)
   }, [])

   const handleLeadSelect = useCallback(
      (lead: Lead) => {
         const storageKey = getStorageKey(
            selectedStageFilter,
            selectedCampaignName
         )
         localStorage.setItem(storageKey, lead.leadId.toString())

         navigate(buildUrlWithParams(lead.leadId))
         onSelectLead(lead.leadId)
      },
      [
         navigate,
         buildUrlWithParams,
         onSelectLead,
         selectedStageFilter,
         selectedCampaignName,
      ]
   )

   // Store current values in refs for stable callback
   const loadingRef = useRef(loadingRecentConversations)
   const hasMoreRef = useRef(hasMore)

   useEffect(() => {
      loadingRef.current = loadingRecentConversations
      hasMoreRef.current = hasMore
   }, [loadingRecentConversations, hasMore])

   // Handle loading more data for infinite scroll
   const handleLoadMore = useCallback(() => {
      if (
         !loadingRef.current &&
         hasMoreRef.current &&
         !isLoadingMoreRef.current
      ) {
         isLoadingMoreRef.current = true
         setPage((prev) => {
            return prev + 1
         })
      }
   }, [])

   // Safe HTML stripping using DOMParser to avoid XSS risks
   const stripHtml = useCallback((html: string) => {
      if (!html) return ''

      try {
         const doc = new DOMParser().parseFromString(html, 'text/html')
         return doc.body.textContent || ''
      } catch {
         // Fallback for very old browsers
         const tmp = document.createElement('DIV')
         tmp.textContent = html
         return tmp.textContent || ''
      }
   }, [])

   // Cleanup on unmount
   useEffect(() => {
      const debouncedFn = debouncedSetSearchRef.current
      return () => {
         debouncedFn.cancel()
         setAccumulatedLeads([])
      }
   }, [])

   if (error) return <div>Error: {error}</div>

   // When selectedCampaignIds is an empty array, it means a campaign filter is active
   // but no campaigns match (e.g., "All Active Campaigns" with zero active campaigns).
   // In this case, show empty results instead of falling back to cached SWR data.
   const isEmptyCampaignFilter =
      Array.isArray(selectedCampaignIds) && selectedCampaignIds.length === 0

   // Use SWR leads as fallback when accumulatedLeads is empty on page 1
   // This handles the case where reset effect clears accumulatedLeads but SWR has cached data
   const displayLeads = isEmptyCampaignFilter
      ? []
      : accumulatedLeads.length > 0
         ? accumulatedLeads
         : (page === 1 && leads?.length ? leads : [])

   const shouldShowSpinner = isEmptyCampaignFilter
      ? false
      : (loadingRecentConversations && (initialLoad || page === 1)) ||
        (isFetching && displayLeads.length === 0)

   return (
      <div className="flex flex-col w-full lg:max-w-[300px] 2xl:max-w-[420px] max-h-full">
         <SearchField
            className="flex py-4 mb-4 border rounded-lg shrink-0 border-primary/40 bg-primary/10"
            placeholder="Search Contacts"
            value={searchInput}
            onChange={handleSearchChange}
         />

         <div className="flex flex-col flex-grow overflow-hidden border rounded-lg bg-primary/10 border-white/10">
            <div className="sticky top-0 z-10 flex px-4 py-2 font-medium border-b bg-primary/10 text-white/70 border-white/10">
               Recent Conversations
            </div>

            {/* <div
               ref={scrollableRef}
               id="lead-list-scrollable"
               className="overflow-y-auto"
            > */}
            {shouldShowSpinner ? (
               <div className="flex items-center justify-center py-8">
                  <Spinner />
               </div>
            ) : (
               <>
                  {displayLeads.length > 0 ? (
                     <InfiniteScroll
                        id="lead-list-scrollable"
                        isLoading={loadingRecentConversations}
                        dataLength={displayLeads.length}
                        next={handleLoadMore}
                        hasMore={hasMore}
                        loader={
                           isLoadingMoreRef.current && (
                              <div className="flex items-center justify-center py-8">
                                 <Spinner />
                              </div>
                           )
                        }
                        scrollThreshold={0.8}
                     >
                        <div className="flex flex-col gap-[2px] px-3 py-4">
                           {displayLeads.map((lead: Lead, index) => (
                              <LeadListItem
                                 key={`${lead.leadId}-${index}`}
                                 lead={lead}
                                 isSelected={lead.leadId === Number(leadId)}
                                 onSelect={handleLeadSelect}
                                 stripHtml={stripHtml}
                              />
                           ))}
                        </div>
                     </InfiniteScroll>
                  ) : (
                     <div className="flex justify-center p-4 text-white/70">
                        No conversation found.
                     </div>
                  )}
               </>
            )}
            {/* </div> */}
         </div>
      </div>
   )
}
